/**
 * API Middleware
 * Centralized middleware for handling API responses, errors, and standardizing communication
 * Based on InversePay API Error & Message Response Documentation
 */

import { showSuccess, showError, showWarning, showInfo } from '../utils/toastHelper';
import { logger } from '../utils/logger';
import { getErrorMessage, formatErrorForDisplay } from '../utils/errorHandler';

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
};

/**
 * Error message mappings for user-friendly display
 */
const ERROR_MESSAGES = {
  // Authentication errors
  'Invalid email or password': 'Email or password is incorrect. Please try again.',
  'Account is suspended': 'Your account has been suspended. Please contact support.',
  'Account is inactive': 'Your account is inactive. Please contact support.',
  'Invalid or expired OTP': 'The code you entered is incorrect or has expired. Please try again.',
  'User not found': 'We couldn\'t find an account with that email.',
  'Account is not an admin account': 'Admin access required. Please use an admin account.',
  
  // Validation errors
  'Email already registered': 'This email is already in use. Try logging in instead.',
  'Phone number already registered': 'This phone number is already in use.',
  'Invalid email format': 'Please enter a valid email address.',
  'Password must be at least 8 characters': 'Password must be at least 8 characters long.',
  'Passwords do not match': 'Passwords do not match. Please check and try again.',
  'Current password is incorrect': 'Current password is incorrect.',
  
  // Resource errors
  'Admin not found': 'Admin not found.',
  'Transaction not found': 'This transaction could not be found.',
  'Notification not found': 'Notification not found.',
  'Application not found': 'Application not found.',
  
  // Permission errors
  'Insufficient permissions': 'You don\'t have permission to access this feature.',
  'Only super admins can create admins': 'Only super admins can create new admins.',
  'Only super admins can delete admins': 'Only super admins can delete admins.',
  'Cannot delete yourself': 'You cannot delete your own account.',
  'Cannot suspend yourself': 'You cannot suspend your own account.',
  
  // Service errors
  'External service unavailable': 'External service is temporarily unavailable. Please try again later.',
  'Exchange rate service unavailable': 'Exchange rate service is temporarily unavailable.',
  'External AML service unavailable': 'AML service is temporarily unavailable.',
  
  // Generic errors
  'Internal server error': 'Something went wrong. Please try again.',
  'Network error occurred': 'Network error. Please check your connection.',
  'Request timeout': 'Request timed out. Please try again.',
};

/**
 * Get user-friendly error message
 */
const getUserFriendlyMessage = (detail) => {
  return ERROR_MESSAGES[detail] || detail;
};

/**
 * Handle rate limiting errors with countdown
 */
const handleRateLimitError = (error) => {
  const waitTime = error.response?.wait_time || 60;
  const message = `Too many requests. Please wait ${waitTime} seconds before trying again.`;
  showWarning(message);
  return {
    success: false,
    error: message,
    waitTime,
    nextAttemptAt: error.response?.next_attempt_at,
  };
};

/**
 * Handle authentication errors
 */
const handleAuthError = (error, status) => {
  // Extract readable error message
  const detail = getErrorMessage(error);
  
  // Clear tokens on authentication failure
  if (typeof window !== 'undefined') {
    if (status === HTTP_STATUS.UNAUTHORIZED) {
      // Don't clear tokens for token refresh failures - let auth service handle it
      if (!detail.includes('refresh')) {
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_user');
      }
    }
  }
  
  const message = getUserFriendlyMessage(detail);
  showError(message);
  
  return {
    success: false,
    error: message,
    status,
    shouldRedirect: status === HTTP_STATUS.UNAUTHORIZED && !detail.includes('refresh'),
  };
};

/**
 * Handle authorization errors
 */
const handleForbiddenError = (error) => {
  const detail = getErrorMessage(error);
  const message = getUserFriendlyMessage(detail);
  showError(message);
  
  return {
    success: false,
    error: message,
    status: HTTP_STATUS.FORBIDDEN,
  };
};

/**
 * Handle validation errors
 */
const handleValidationError = (error) => {
  const detail = getErrorMessage(error);
  const message = getUserFriendlyMessage(detail);
  showError(message);
  
  return {
    success: false,
    error: message,
    status: HTTP_STATUS.BAD_REQUEST,
    validationErrors: error.errors || [],
  };
};

/**
 * Handle not found errors
 */
const handleNotFoundError = (error) => {
  const detail = getErrorMessage(error);
  const message = getUserFriendlyMessage(detail);
  showError(message);
  
  return {
    success: false,
    error: message,
    status: HTTP_STATUS.NOT_FOUND,
  };
};

/**
 * Handle conflict errors
 */
const handleConflictError = (error) => {
  const detail = getErrorMessage(error);
  const message = getUserFriendlyMessage(detail);
  showError(message);
  
  return {
    success: false,
    error: message,
    status: HTTP_STATUS.CONFLICT,
  };
};

/**
 * Handle server errors
 */
const handleServerError = (error, status) => {
  const detail = getErrorMessage(error);
  const message = status === HTTP_STATUS.BAD_GATEWAY 
    ? getUserFriendlyMessage('External service unavailable')
    : getUserFriendlyMessage('Internal server error');
  
  showError(message);
  logger.error('Server error:', { status, detail, error });
  
  return {
    success: false,
    error: message,
    status,
  };
};

/**
 * Main error handler - routes errors to appropriate handlers
 */
export const handleApiError = (error, options = {}) => {
  const { suppressToast = false, customMessage = null } = options;
  
  // Extract status and detail from error using error handler
  const status = error.status || error.response?.status_code || HTTP_STATUS.INTERNAL_SERVER_ERROR;
  const detail = getErrorMessage(error, 'An error occurred');
  
  logger.error('API Error:', { status, detail, error });
  
  // If custom message provided and toast not suppressed, show it
  if (customMessage && !suppressToast) {
    showError(customMessage);
    return {
      success: false,
      error: customMessage,
      status,
    };
  }
  
  // Route to appropriate handler based on status
  let result;
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      result = handleValidationError(error);
      break;
      
    case HTTP_STATUS.UNAUTHORIZED:
      result = handleAuthError(error, status);
      break;
      
    case HTTP_STATUS.FORBIDDEN:
      result = handleForbiddenError(error);
      break;
      
    case HTTP_STATUS.NOT_FOUND:
      result = handleNotFoundError(error);
      break;
      
    case HTTP_STATUS.CONFLICT:
      result = handleConflictError(error);
      break;
      
    case HTTP_STATUS.TOO_MANY_REQUESTS:
      result = handleRateLimitError(error);
      break;
      
    case HTTP_STATUS.BAD_GATEWAY:
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      result = handleServerError(error, status);
      break;
      
    default:
      const message = getUserFriendlyMessage(detail);
      if (!suppressToast) {
        showError(message);
      }
      result = {
        success: false,
        error: message,
        status,
      };
  }
  
  // Suppress toast if requested (useful for silent error handling)
  if (suppressToast) {
    // Don't show toast, but still return error info
    return result;
  }
  
  return result;
};

/**
 * Handle successful API responses
 */
export const handleApiSuccess = (response, options = {}) => {
  const {
    suppressToast = false,
    customMessage = null,
    showToast = true,
  } = options;
  
  // Extract success message from response
  const message = customMessage || response.message || 'Operation completed successfully';
  
  // Show success toast if not suppressed
  if (showToast && !suppressToast && message) {
    showSuccess(message);
  }
  
  logger.info('API Success:', { message, response });
  
  return {
    success: true,
    data: response.data || response,
    message,
  };
};

/**
 * Wrapper for API calls with automatic error/success handling
 */
export const withApiMiddleware = async (apiCall, options = {}) => {
  const {
    onSuccess = null,
    onError = null,
    successMessage = null,
    errorMessage = null,
    suppressSuccessToast = false,
    suppressErrorToast = false,
    showLoadingToast = false,
    loadingMessage = 'Processing...',
  } = options;
  
  try {
    // Show loading toast if requested
    if (showLoadingToast) {
      showInfo(loadingMessage);
    }
    
    // Make API call
    const response = await apiCall();
    
    // Handle success
    const result = handleApiSuccess(response, {
      customMessage: successMessage,
      suppressToast: suppressSuccessToast,
    });
    
    // Call success callback if provided
    if (onSuccess) {
      onSuccess(result.data);
    }
    
    return result;
  } catch (error) {
    // Handle error
    const result = handleApiError(error, {
      customMessage: errorMessage,
      suppressToast: suppressErrorToast,
    });
    
    // Call error callback if provided
    if (onError) {
      onError(result);
    }
    
    // Re-throw error for caller to handle if needed
    throw error;
  }
};

/**
 * Validate response format
 */
export const validateResponse = (response) => {
  // Check for success response format
  if (response.success !== undefined) {
    return {
      isValid: true,
      format: 'success',
      data: response.data,
      message: response.message,
    };
  }
  
  // Check for error response format
  if (response.detail !== undefined && response.status_code !== undefined) {
    return {
      isValid: true,
      format: 'error',
      error: response.detail,
      status: response.status_code,
    };
  }
  
  // Check for OTP response format
  if (response.message && response.otp_id !== undefined) {
    return {
      isValid: true,
      format: 'otp',
      message: response.message,
      otpId: response.otp_id,
    };
  }
  
  // Unknown format but might still be valid data
  return {
    isValid: true,
    format: 'unknown',
    data: response,
  };
};

/**
 * Extract pagination info from response
 */
export const extractPagination = (response) => {
  return {
    total: response.total || 0,
    page: response.page || 1,
    limit: response.limit || 10,
    pages: response.pages || Math.ceil((response.total || 0) / (response.limit || 10)),
  };
};

/**
 * Format error for logging/debugging
 */
export const formatErrorForLogging = (error) => {
  return {
    message: error.message || error.detail || 'Unknown error',
    status: error.status || error.response?.status_code,
    stack: error.stack,
    response: error.response,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error) => {
  const status = error.status || error.response?.status_code;
  
  // Retry on network errors, timeouts, and 5xx errors
  return (
    status === 0 || // Network error
    status === 408 || // Timeout
    status === 429 || // Rate limit (with backoff)
    status >= 500 // Server errors
  );
};

/**
 * Get retry delay based on error type
 */
export const getRetryDelay = (error, attemptNumber = 1) => {
  const status = error.status || error.response?.status_code;
  
  // For rate limiting, use the wait_time from response
  if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    return (error.response?.wait_time || 60) * 1000;
  }
  
  // Exponential backoff for other retryable errors
  // 1s, 2s, 4s, 8s, etc.
  return Math.min(1000 * Math.pow(2, attemptNumber - 1), 30000);
};

/**
 * Retry API call with exponential backoff
 */
export const retryApiCall = async (apiCall, options = {}) => {
  const {
    maxRetries = 3,
    onRetry = null,
  } = options;
  
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      
      // Check if error is retryable
      if (!isRetryableError(error)) {
        throw error;
      }
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Calculate delay
      const delay = getRetryDelay(error, attempt);
      
      logger.warn(`API call failed, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`, {
        error: formatErrorForLogging(error),
      });
      
      // Call retry callback if provided
      if (onRetry) {
        onRetry(attempt, delay);
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // All retries failed
  throw lastError;
};

export default {
  handleApiError,
  handleApiSuccess,
  withApiMiddleware,
  validateResponse,
  extractPagination,
  formatErrorForLogging,
  isRetryableError,
  getRetryDelay,
  retryApiCall,
  HTTP_STATUS,
};
